import { SearchSettingLanguage, TranslateSpecificField, type ApiSearchSettingLanguageRequest, type ApiTranslateSpecificFieldResponse } from '@/infrastructure/api-functions/core-search-api/core-search-api';
import { batchProxy } from '@/utils/flow';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { SelectedLanguageState, TranslateConfigService, TranslateOptions, TranslateResult, TranslateService } from './translate-service.interface';
import { LangTypeMode } from '@/domains/translation/enums';
import { DEFAULT_LANGUAGE } from '@/domains/translation/constants';
import { GetIsFreeViewService } from '@/services/common/get-is-free-view.interface';

@injectable()
export class TranslateServiceImpl implements TranslateService, TranslateConfigService {
    private _batchFn: (options: {
        patentId: string;
        translatedLanguage: string;
        field: string;
    }[]) => Promise<ApiTranslateSpecificFieldResponse> ;

    constructor(
        @inject(TranslateSpecificField)
        private readonly _translateSpecificField: TranslateSpecificField,
        @inject(SearchSettingLanguage)
        private readonly _searchSettingLanguage: SearchSettingLanguage,
        @inject(GetIsFreeViewService)
        private _getIsFree: GetIsFreeViewService,
    ) {
        this._batchFn = batchProxy((options: {
            patentId: string;
            translatedLanguage: string;
            field: string;
        }[]) => {
            return this._translateSpecificField.translateSpecificField({
                patent_ids: options.map(item => item.patentId),
                fields: [options[0].field],
                trans_lang: options[0].translatedLanguage,
            });
        }, 200);
    }

    async getSpecificField(options: TranslateOptions): Promise<TranslateResult> {

        const data = await this._batchFn([{
            patentId: options.patentId,
            translatedLanguage: options.translatedLanguage,
            field: options.field,
        }]);

        const res = data.filter(item => item.patent_id === options.patentId);

        if (res.length === 0) {
            throw new Error('No data found');
        }

        const result = {
            text: res[0].title,
            translatedText: res[0].title_tran,
            originalLanguage: res[0].title_lang,
            translatedLanguage: res[0].title_tran_lang || options.translatedLanguage,
        };

        return result;
    }

    async getSelectedLanguage(input: { workspaceId?: string; }) {
        let params = {};
        if (input.workspaceId) {
            params = {
                source: 'workspace',
                workspace_id: input.workspaceId,
            };
        } else {
            params = {
                source: 'analytics',
            };
        }
        const isFree = this._getIsFree.getIsFreeView();

        const data = isFree ?
            { language: 'default' }
            : await this._searchSettingLanguage.getSelectedLanguage(params as ApiSearchSettingLanguageRequest);

        const language = (data.language === 'default' ? DEFAULT_LANGUAGE : data.language) as SelectedLanguageState['language'];
        const langType = (data.language === 'default' ? LangTypeMode.DEFAULT : LangTypeMode.TRANSLATION);

        return {
            language,
            langType,
        };
    }
}
